import { useCallback, useMemo, useState } from "react";
import { T, useT } from "@transifex/react";
import { z } from "zod";
import { Box, CircularProgress } from "@material-ui/core";
import { Modal } from "storybook/components/modal";
import { Button } from "storybook/components/button";
import { Notification } from "storybook/components/notification";
import { NAVIGATOR_HOSTNAME, SPECIALIST_HOSTNAME } from "env-vars";
import { useForm } from "storybook/utils/form";
import { useFeatureFlags } from "context/admin-content";
import { RiskGuideline } from "../payload-helper";
import {
  DeclineButtons,
  BannerContainer,
  ModalBodyWrapper,
  GlobalModalStyles,
} from "../model.styles";

type Mode = "breast-mri" | "genetic-testing" | "lung-ct";

type Props = {
  mode: Mode;
  onClose: (mode: Mode) => void;
  onSuccessfulDecline: (mode: Mode) => void;
};

enum DeclineReason {
  AlreadyBeingFollowed = "Already Being Followed",
  DoesNotWantToKnow = "Does Not Want to Know",
  HadGeneticCounselingTesting = "Had Genetic Counseling/Testing",
  FamilyMemberTesting = "Family Member Testing",
  Insurance = "Insurance",
  NoResponse = "No Response",
  TimeConstraints = "Time Constraints",
  Transportation = "Transportation",
  OtherReason = "Other Reason",
  NotApplicable = "Not Applicable",
}

const getDeclineReasons = (
  t: (str: string) => string
): Array<{ value: string; label: string }> => [
  {
    value: DeclineReason.AlreadyBeingFollowed,
    label: t("Already Being Followed"),
  },
  {
    value: DeclineReason.DoesNotWantToKnow,
    label: t("Does Not Want to Know"),
  },
  {
    value: DeclineReason.HadGeneticCounselingTesting,
    label: t("Had Genetic Counseling / Testing"),
  },
  {
    value: DeclineReason.FamilyMemberTesting,
    label: t("Family Member Testing"),
  },
  {
    value: DeclineReason.Insurance,
    label: t("Insurance"),
  },
  {
    value: DeclineReason.NoResponse,
    label: t("No Response"),
  },
  {
    value: DeclineReason.TimeConstraints,
    label: t("Time Constraints"),
  },
  {
    value: DeclineReason.Transportation,
    label: t("Transportation"),
  },
  {
    value: DeclineReason.OtherReason,
    label: t("Other Reason"),
  },
  {
    value: DeclineReason.NotApplicable,
    label: t("Not Applicable"),
  },
];

const schema = z.object({
  declineReason: z.enum(Object.values(DeclineReason) as [string, ...string[]]),
});

type SchemaType = z.infer<typeof schema>;

const RISK_GUIDELINE_MAP = {
  "genetic-testing": RiskGuideline.TESTING,
  "breast-mri": RiskGuideline.MRI,
  "lung-ct": RiskGuideline.LUNG,
};

export function DeclineModal({ mode, onClose, onSuccessfulDecline }: Props) {
  const t = useT();
  const featureFlags = useFeatureFlags();
  const [error, setError] = useState<boolean>(false);
  const { Form, Radio, getValues } = useForm<SchemaType>({ schema });
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const reasons = useMemo(() => getDeclineReasons(t), [t]);
  const value = getValues("declineReason");

  const handleClose = useCallback(() => {
    onClose(mode);
  }, [onClose, mode]);

  const handleSubmit = useCallback(
    async ({ declineReason }: { declineReason: string }) => {
      setError(false);
      setSubmitting(true);

      try {
        const idToken = localStorage.getItem("id_token");
        const patientId = localStorage.getItem("ciq_id") || "";
        const auth0UserEmail = localStorage.getItem("ciq_email") || "";

        const personContactHistory = {
          actionDateTime: Math.floor(new Date().getTime()),
          // If using navigator, 17 means IN_PERSON
          actionType: featureFlags.removeAccessToNavigatorApp ? null : 17,
          in_person_reason: "declined_appointment",
          user_email: auth0UserEmail,
          decline_reason: declineReason,
        };
        const isGeneticTesting = mode === "genetic-testing";
        const isNavigatorGeneticTesting =
          !featureFlags.removeAccessToNavigatorApp && isGeneticTesting;
        const endpoint = isNavigatorGeneticTesting
          ? NAVIGATOR_HOSTNAME
          : SPECIALIST_HOSTNAME;
        const personStatus = isNavigatorGeneticTesting ? { status: "In Person: Declined" } : { status: "Declined" };
        const method = isNavigatorGeneticTesting ? "POST" : "PUT";
        const body = {
          person: {
            person_statuses_attributes: [
              isNavigatorGeneticTesting
                ? personStatus
                : {
                    ...personStatus,
                    risk_guideline: RISK_GUIDELINE_MAP[mode],
                  },
            ],
            person_contact_histories_attributes: [
              isNavigatorGeneticTesting
                ? personContactHistory
                : {
                    ...personContactHistory,
                    risk_guideline: RISK_GUIDELINE_MAP[mode],
                    method: 4, // means declined via patient portal
                  },
            ],
          },
        };

        const response = await fetch(`${endpoint}/api/v1/people/${patientId}`, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error("Failed to submit form");
        }

        onSuccessfulDecline(mode);
      } catch (err) {
        console.error("Error submitting form:", err);
        setError(true);
      } finally {
        setSubmitting(false);
      }
    },
    [mode, featureFlags.removeAccessToNavigatorApp, onSuccessfulDecline]
  );

  return (
    <>
      <GlobalModalStyles />
      <Modal theme="default" open={true} onClose={handleClose} width={600}>
        <Modal.Head alignTitle="left">
          <T _str="Please select a reason" />
        </Modal.Head>
        <Form onSubmit={handleSubmit} id="decline-reasons">
          <Modal.Body>
            <ModalBodyWrapper>
              {error && (
                <BannerContainer>
                  <Notification
                    theme="default"
                    variant="warning"
                    fullWidth={true}
                    title={t("An error occurred. Please try again later.")}
                  />
                </BannerContainer>
              )}
              <Box
                display="flex"
                flexDirection="column"
                style={{ rowGap: "1rem" }}
              >
                {reasons.map((reason) => (
                  <Radio
                    theme="default"
                    name="declineReason"
                    key={reason.value}
                    value={reason.value}
                    displayValue={<span>{reason.label}</span>}
                  />
                ))}
              </Box>
            </ModalBodyWrapper>
          </Modal.Body>
          <Modal.Actions>
            {isSubmitting ? (
              <CircularProgress />
            ) : (
              <DeclineButtons>
                <Button
                  theme="default"
                  variant="text"
                  color="default"
                  onClick={handleClose}
                >
                  <T _str="Cancel" />
                </Button>
                <Button
                  color="default"
                  variant="contained"
                  theme="default"
                  type="submit"
                  disabled={!value}
                >
                  <T _str="Ok" />
                </Button>
              </DeclineButtons>
            )}
          </Modal.Actions>
        </Form>
      </Modal>
    </>
  );
}
